import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateDiscountRequestSchema,
  type CreateDiscountRequest,
  type DiscountDetail,
  type DiscountListItem,
  UpdateDiscountRequestSchema,
  type UpdateDiscountRequest,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSwitch,
} from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateDiscount, useUpdateDiscount } from '../use-discounts';

import { deriveCode, useAutoDeriveCode } from '@/components/master-code-field';

interface DiscountFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  discount?: DiscountDetail | DiscountListItem | null;
}

const CREATE_DEFAULTS: CreateDiscountRequest = {
  code: '',
  name: '',
  amountPaise: '0',
  sortOrder: 0,
  isActive: true,
};

const paiseToRupees = (paise: string | undefined): number | '' => {
  const n = Number(paise ?? '0');
  if (!Number.isFinite(n) || n === 0) return '';
  return n / 100;
};
const rupeesToPaise = (rupees: number | null): string =>
  rupees === null ? '0' : String(Math.round(rupees * 100));

const DiscountFields = ({ pending }: { pending: boolean }): React.JSX.Element => (
  <>
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Name"
          placeholder="e.g. Early bird discount"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="amountPaise">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Discount amount (Rs)"
          precision={2}
          min={0}
          leftAdornment={<span className="text-sm text-zinc-500">₹</span>}
          value={paiseToRupees(field.value as string | undefined)}
          onValueChange={(v) => field.onChange(rupeesToPaise(v))}
          error={error}
          required
          disabled={pending}
          hint="Fixed rupee amount subtracted from the grand total on the Pricing step."
        />
      )}
    </UzFormField>
    <UzFormField name="sortOrder">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Sort order"
          precision={0}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isActive">
      {({ field }) => (
        <UzSwitch
          label="Active"
          description="Inactive rows are hidden from the Pricing step dropdown."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
  </>
);

export const DiscountFormDrawer = ({
  open,
  onOpenChange,
  mode,
  discount,
}: DiscountFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createMut = useCreateDiscount();
  const updateMut = useUpdateDiscount();
  const pending = createMut.isPending || updateMut.isPending;

  const createForm = useForm<CreateDiscountRequest>({
    resolver: zodResolver(CreateDiscountRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateDiscountRequest>({
    resolver: zodResolver(UpdateDiscountRequestSchema),
    defaultValues: {},
  });

  useAutoDeriveCode(createForm, isCreate);

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (discount) {
      updateForm.reset({
        name: discount.name,
        amountPaise: discount.amountPaise,
        sortOrder: discount.sortOrder,
        isActive: discount.isActive,
      });
    }
  }, [open, isCreate, discount, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    await createMut.mutateAsync({
      ...values,
      code: deriveCode(values.name),
    });
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!discount) return;
    await updateMut.mutateAsync({ id: discount.id, payload: values });
    onOpenChange(false);
  });

  const fields = <DiscountFields pending={pending} />;

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add discount' : `Edit ${discount?.name ?? 'discount'}`}
      description="The name is what agents see. The amount is a fixed rupee value subtracted from the grand total."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
